/**
 * OPDS Catalog Storage
 * Manages user's OPDS catalog subscriptions in LocalStorage
 */

export interface UserOPDSCatalog {
  id: string;
  name: string;
  url: string;
  description?: string;
  language?: string;
  addedAt: string;
  lastAccessed?: string;
}

const STORAGE_KEY = 'legere:opds:catalogs';

export class OPDSCatalogStorage {
  /**
   * Get all user's OPDS catalogs
   */
  static getAll(): UserOPDSCatalog[] {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return [];

    try {
      return JSON.parse(data);
    } catch {
      return [];
    }
  }

  /**
   * Add new OPDS catalog
   */
  static add(catalog: Omit<UserOPDSCatalog, 'id' | 'addedAt'>): UserOPDSCatalog {
    const catalogs = this.getAll();

    // Check if catalog already exists by URL
    const existing = catalogs.find((c) => c.url === catalog.url);
    if (existing) {
      return existing;
    }

    const newCatalog: UserOPDSCatalog = {
      ...catalog,
      id: crypto.randomUUID(),
      addedAt: new Date().toISOString(),
    };

    catalogs.push(newCatalog);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(catalogs));

    return newCatalog;
  }

  /**
   * Remove OPDS catalog by ID
   */
  static remove(id: string): void {
    const catalogs = this.getAll().filter((c) => c.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(catalogs));
  }

  /**
   * Update last accessed timestamp
   */
  static updateLastAccessed(id: string): void {
    const catalogs = this.getAll();
    const catalog = catalogs.find((c) => c.id === id);

    if (catalog) {
      catalog.lastAccessed = new Date().toISOString();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(catalogs));
    }
  }

  /**
   * Update catalog metadata
   */
  static update(id: string, updates: Partial<UserOPDSCatalog>): void {
    const catalogs = this.getAll();
    const catalog = catalogs.find((c) => c.id === id);

    if (catalog) {
      Object.assign(catalog, updates);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(catalogs));
    }
  }

  /**
   * Check if catalog exists by URL
   */
  static exists(url: string): boolean {
    return this.getAll().some((c) => c.url === url);
  }

  /**
   * Clear all catalogs
   */
  static clear(): void {
    localStorage.removeItem(STORAGE_KEY);
  }
}
